import { StepObjectVisibility, TimelineStep } from '../types/timeline';

interface StoredTimelineState {
  version: 1;
  modelId: string;
  steps: TimelineStep[];
  visibilityAssignments: StepObjectVisibility[];
  lastFrame: number;
  updatedAt: string;
}

const STORAGE_PREFIX = 'timeline-editor';

function getBasenameWithoutExtension(pathLike: string): string {
  const withoutQuery = pathLike.split('?')[0]?.split('#')[0] ?? pathLike;
  const segments = withoutQuery.split('/').filter(Boolean);
  const last = segments[segments.length - 1] ?? withoutQuery;
  return last.replace(/\.[^.]+$/, '');
}

function getCustomModelFamily(rawCustomGlbUrl: string): string {
  const candidate = (() => {
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const parsed = new URL(rawCustomGlbUrl, base);
      return getBasenameWithoutExtension(parsed.pathname || rawCustomGlbUrl);
    } catch {
      return getBasenameWithoutExtension(rawCustomGlbUrl);
    }
  })();

  const normalized = candidate.toLowerCase().trim();
  if (!normalized) return 'custom';

  const family = normalized.split(/[-_\s]+/)[0] ?? normalized;
  return family || normalized;
}

function inferModelFamily(modelId: string): string {
  if (!modelId) return '';

  if (modelId.startsWith('custom:')) {
    return modelId.slice('custom:'.length).toLowerCase();
  }

  if (modelId.startsWith('model:')) {
    return modelId.slice('model:'.length).toLowerCase();
  }

  return getCustomModelFamily(modelId);
}

function sanitizeModelId(rawModelId: string): string {
  return rawModelId.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 180);
}

export function buildTimelineStorageKey(rawModelId: string): string {
  return `${STORAGE_PREFIX}:${sanitizeModelId(rawModelId)}`;
}

export function resolveTimelineModelId(params: {
  customGlbUrl: string | null;
  modelPath?: string;
  modelKey?: string;
  uploadedModelId?: string | null;
}): string {
  const { customGlbUrl, modelPath, modelKey, uploadedModelId } = params;
  if (uploadedModelId) return uploadedModelId;
  if (customGlbUrl) return `custom:${getCustomModelFamily(customGlbUrl)}`;
  if (modelPath) return modelPath;
  if (modelKey) return `model:${modelKey}`;
  return 'model:default';
}

export function loadTimelineState(storageKey: string): StoredTimelineState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredTimelineState;
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.steps)) {
      return null;
    }

    return {
      ...parsed,
      visibilityAssignments: Array.isArray(parsed.visibilityAssignments)
        ? parsed.visibilityAssignments
        : [],
    };
  } catch (error) {
    console.warn('Failed to parse timeline state from localStorage:', error);
    return null;
  }
}

export function saveTimelineState(
  storageKey: string,
  modelId: string,
  payload: { steps: TimelineStep[]; lastFrame: number; visibilityAssignments?: StepObjectVisibility[] }
): void {
  if (typeof window === 'undefined') return;

  const validStepIds = new Set(payload.steps.map((step) => step.id));
  const prunedVisibilityAssignments = (payload.visibilityAssignments ?? []).filter((assignment) =>
    validStepIds.has(assignment.stepId)
  );

  const nextState: StoredTimelineState = {
    version: 1,
    modelId,
    steps: payload.steps,
    visibilityAssignments: prunedVisibilityAssignments,
    lastFrame: payload.lastFrame,
    updatedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(nextState));
  } catch (error) {
    console.warn('Failed to save timeline state to localStorage:', error);
  }
}

export function loadLatestTimelineStateForModelFamily(
  storageKey: string,
  modelId: string
): StoredTimelineState | null {
  if (typeof window === 'undefined') return null;

  const family = inferModelFamily(modelId);
  if (!family) return null;

  let best: StoredTimelineState | null = null;
  let bestUpdatedAt = -1;
  let bestHasSteps = false;

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key || key === storageKey || !key.startsWith(`${STORAGE_PREFIX}:`)) continue;

    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw) as StoredTimelineState;
      if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.steps)) continue;
      if (inferModelFamily(parsed.modelId) !== family) continue;

      const normalizedParsed: StoredTimelineState = {
        ...parsed,
        visibilityAssignments: Array.isArray(parsed.visibilityAssignments)
          ? parsed.visibilityAssignments
          : [],
      };

      const updatedAtMs = Date.parse(normalizedParsed.updatedAt || '') || 0;
      const hasSteps = normalizedParsed.steps.length > 0;

      const shouldReplace =
        !best ||
        (hasSteps && !bestHasSteps) ||
        (hasSteps === bestHasSteps && updatedAtMs > bestUpdatedAt);

      if (shouldReplace) {
        best = normalizedParsed;
        bestUpdatedAt = updatedAtMs;
        bestHasSteps = hasSteps;
      }
    } catch {
      // Ignore malformed entries
    }
  }

  return best;
}
